import React, { useState } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { MediaRenderer } from './ImagePlaceholder';
import { SocialIcon } from './SocialIcons';
import type { ArtistProfileHeaderProps } from '@/features/artists/types/types';

/**
 * Artist profile header component with banner and profile info
 */
export const ArtistProfileHeader: React.FC<ArtistProfileHeaderProps> = ({ artist }) => {
  const [isHovered, setIsHovered] = useState(false);
  const pfp = artist.media?.find(m => m.rank === 0);

  const mediaVariants = {
    initial: { opacity: 0, scale: 1.05 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 1.05 },
  };

  return (
    <header className="relative">
      {/* Banner Image/Video */}
      <div 
        className="h-[30vh] md:h-[40vh] bg-black relative overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <AnimatePresence initial={false}>
          {!isHovered ? (
            <motion.div key="image" {...mediaVariants} className="h-full w-full">
              <MediaRenderer 
                mediaId={artist.card_image_id} 
                alt={`${artist.name} banner`}
                className="!rounded-none h-full w-full object-cover opacity-50" 
              />
            </motion.div>
          ) : (
            <motion.div key="video" {...mediaVariants} className="h-full w-full">
              <MediaRenderer 
                mediaId={artist.card_video_id} 
                alt={`${artist.name} video`}
                className="!rounded-none h-full w-full object-cover opacity-50"
                isVideo={true}
                autoPlay={true}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Profile Info */}
      <div className="max-w-5xl mx-auto px-8">
        <div className="flex flex-col md:flex-row items-center md:items-end -mt-20 md:-mt-24 relative z-10">
          {/* Circular PFP */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-black flex-shrink-0 bg-gray-900"
          >
            {pfp && (
              <MediaRenderer 
                mediaId={pfp.media_id} 
                alt={`${artist.name} profile`}
                className="!rounded-full" 
              />
            )}
          </motion.div>

          {/* Artist Details */}
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
            className="w-full text-center md:text-left md:ml-8 mt-6 md:mt-0"
          >
            <h1 className="text-4xl md:text-6xl font-serif font-medium tracking-tight text-white">
              {artist.name}
            </h1>
            <div className="flex flex-wrap gap-3 mt-4 justify-center md:justify-start">
              {artist.music_genres?.map(genre => (
                <span key={genre} className="px-3 py-1 bg-gray-800 text-gray-300 font-medium rounded-full text-xs">
                  {genre}
                </span>
              ))}
            </div>
            {/* Social Media Links */}
            {artist.social_media_links && Object.keys(artist.social_media_links).length > 0 && (
              <div className="flex items-center gap-3 mt-5 justify-center md:justify-start flex-wrap">
                {Object.entries(artist.social_media_links).map(([platform, url]) => (
                  <a 
                    key={platform} 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 bg-gray-900/80 border border-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600/50 hover:border-blue-500 transition-all duration-300"
                  >
                    <SocialIcon platform={platform} />
                  </a>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </header>
  );
};
